import React, { useState } from "react"
import {
  Box,
  Text,
  Flex,
  Heading,
  Button,
  Label,
  Checkbox,
  Input,
  Grid,
  Image,
} from "@theme-ui/components"
import { i18nContext } from "../context/i18nContext"
import axios from "axios"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import ArrowRight from "../images/arrow-right.svg"

const Survey = ({ survey, formData, setFormData }) => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [surveyAnswers, setSurveyAnswers] = useState({})
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [showValidationError, setShowValidationError] = useState(false)

  const handleAnswerChange = (questionId, answerId, answerText, isMultiple = false) => {
    if (showValidationError) {
      setShowValidationError(false)
    }

    if (isMultiple) {
      setSurveyAnswers(prev => ({
        ...prev,
        [questionId]: prev[questionId] && prev[questionId].includes(answerId)
          ? prev[questionId].filter(id => id !== answerId)
          : [...(prev[questionId] || []), answerId],
      }))
    } else {
      setSurveyAnswers(prev => ({
        ...prev,
        [questionId]: answerId,
      }))
    }
  }

  const validateSurvey = () => {
    if (!survey || !survey.questions) {
      return false
    }
    for (const question of survey.questions) {
      const answer = surveyAnswers[question.id]
      if (!answer || (Array.isArray(answer) && answer.length === 0)) {
        return false
      }
    }
    return privacyAccepted
  }

  const prepareSurveyDataForEmail = () => {
    if (!survey || !survey.questions) {
      return []
    }
    const questionsWithAnswers = survey.questions.map(question => {
      const selectedAnswerIds = surveyAnswers[question.id]
      let selectedAnswerTexts = []

      if (Array.isArray(selectedAnswerIds)) {
        selectedAnswerTexts = selectedAnswerIds.map(answerId =>
          question.answers.find(answer => answer.id === answerId)?.text
        ).filter(Boolean)
      } else {
        const selectedAnswer = question.answers.find(answer => answer.id === selectedAnswerIds)
        selectedAnswerTexts = selectedAnswer ? [selectedAnswer.text] : []
      }

      return {
        questionText: question.text,
        answerTexts: selectedAnswerTexts,
      }
    })

    return questionsWithAnswers
  }

  const sendMail = async event => {
    event.preventDefault()

    if (!validateSurvey()) {
      setShowValidationError(true)
      return
    }

    setShowValidationError(false)
    const result = await executeRecaptcha("dynamicAction")

    setLoading(true)

    const data = {
      ...formData,
      survey: {
        title: survey?.title || "",
        questions: prepareSurveyDataForEmail(),
      },
    }

    if (result)
      axios
        .post("/.netlify/functions/sendQuestionnaireForm", data)
        .then(function () {
          setSuccess(true)
          setLoading(false)

          if (typeof window !== "undefined" && window.dataLayer !== undefined) {
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({
              event: "submit_blog_form",
            })
          }
        })
        .catch(function () {
          setLoading(false)
        })
    else setLoading(false)
  }

  const renderQuestion = (question, index) => {
    const paddedIndex = (index + 1).toString().padStart(2, "0")
    const isRadio = question.controlType === "Radio"
    const isCheckbox = question.controlType === "Checkbox"

    return (
      <Box key={question.id}>
        <Text
          variant="kicker"
          sx={{
            px: "15px",
            py: "10px",
            mb: "20px",
            mt: ["0px", "0px", "0px", "0px"],
            display: "block",
            fontSize: "15px",
            fontWeight: "500",
          }}
        >
          {paddedIndex}
        </Text>
        <Text
          sx={{
            fontSize: "24px",
            fontWeight: 400,
            lineHeight: "1.2",
            color: "dark",
          }}
        >
          {question.text}
        </Text>

        {isRadio && (
          <Flex sx={{ gap: 3, my: "60px" }}>
            {question.answers.map(answer => (
              <Button
                key={answer.id}
                onClick={() =>
                  !loading && handleAnswerChange(question.id, answer.id, answer.text)
                }
                disabled={loading}
                sx={{
                  backgroundColor:
                    surveyAnswers[question.id] === answer.id
                      ? "primary"
                      : "transparent",
                  color:
                    surveyAnswers[question.id] === answer.id
                      ? "light"
                      : "dark",
                  border: "1px solid",
                  borderColor: "primary",
                  borderRadius: "32px",
                  px: ["40px", "40px", "90px", "90px"],
                  py: ["20px", "20px", "24px", "24px"],
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: "16px",
                  textTransform: "uppercase",
                  fontWeight: 400,
                  opacity: loading ? 0.6 : 1,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: loading ? "transparent" : "primary",
                    color: loading ? "dark" : "white",
                  },
                }}
              >
                {answer.text}
              </Button>
            ))}
          </Flex>
        )}

        {isCheckbox && (
          <Box sx={{ mt: "40px", mb: "60px" }}>
            {question.answers.map(answer => (
              <Label
                key={answer.id}
                onClick={() =>
                  !loading &&
                  handleAnswerChange(question.id, answer.id, answer.text, true)
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: "20px",
                  gap: ["20px", "20px", "20px", "20px"],
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: ["18px", "18px", "20px", "20px"],
                  fontWeight: 400,
                  color: "dark",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <Box
                  sx={{
                    width: "30px",
                    height: "30px",
                    minWidth: "30px",
                    minHeight: "30px",
                    flexShrink: 0,
                    borderRadius: "4px",
                    border: "2px solid",
                    borderColor: "primary",
                    backgroundColor: (surveyAnswers[question.id] || []).includes(answer.id)
                      ? "primary"
                      : "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      backgroundColor: "primary",
                      color: "white",
                    },
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  {(surveyAnswers[question.id] || []).includes(answer.id) && (
                    <Text
                      sx={{
                        color: "white",
                        fontSize: "18px",
                        fontWeight: "bold",
                        lineHeight: 1,
                      }}
                    >
                      ✓
                    </Text>
                  )}
                </Box>
                {answer.text}
              </Label>
            ))}
          </Box>
        )}
      </Box>
    )
  }

  const LabeledCheckbox = ({ children, checked, onChange, ...props }) => {
    return (
      <Label
        sx={{
          display: "grid",
          gridTemplateColumns: "25px 1fr",
          gap: "20px",
          color: "text",
          svg: {
            color: "dark",
            width: "25px",
            height: "25px",
          },
        }}
      >
        <Checkbox
          sx={{}}
          checked={checked}
          onChange={onChange}
          disabled={loading}
          {...props}
        />
        {children}
      </Label>
    )
  }

  if (!survey || !survey.questions || survey.questions.length === 0) {
    return null
  }

  return (
    <i18nContext.Consumer>
      {t => (
        <Box sx={{ mt: ["40px", "40px", "60px"] }}>
          <Text
            variant="kicker"
            sx={{
              display: "block",
              px: "15px",
              py: "10px",
              m: ["0px", "0px", "0px", "0px"],
              fontSize: "15px",
              fontWeight: 500,
            }}
          >
            {survey.kicker}
          </Text>

          <Heading
            as="h3"
            sx={{
              mt: "60px",
              fontSize: "33px",
              color: "dark",
            }}
          >
            {survey.title}
          </Heading>

          <Box sx={{ mt: "60px" }}>
            {survey.questions.map((question, index) =>
              renderQuestion(question, index)
            )}
          </Box>

          <Text
            variant="kicker"
            sx={{
              px: "15px",
              py: "10px",
              mb: "60px",
              mt: ["0px", "0px", "0px", "0px"],
              display: "block",
              fontSize: "15px",
              fontWeight: "500",
            }}
          >
            {(survey.questions.length + 1).toString().padStart(2, "0")}
          </Text>

          <form
            style={{ paddingLeft: "15px", paddingRight: "15px" }}
            onSubmit={sendMail}
          >
            <Box
              sx={{
                input: {
                  position: "relative",
                  borderColor: "dark",
                  borderRadius: 0,
                  borderTop: "none",
                  borderLeft: "none",
                  borderRight: "none",
                  borderBottom: "2px solid",
                  pt: 4,
                  fontSize: "25px",
                  "&::placeholder": {
                    fontSize: "21px!important",
                    fontWeight: 500,
                    color: "dark",
                    position: "absolute",
                    top: "0px",
                  },
                },
              }}
            >
              <Grid
                sx={{
                  flexWrap: "wrap",
                  flexDirection: "column",
                  pb: [0, 0, 0, 0],
                }}
                gap={[0, 0, "40px", "40px"]}
              >
                <Grid columns={[1, 1, 2]} gap={[0, 0, 3]}>
                  <Box sx={{ pb: [4, 4, 4, 4] }}>
                    <Input
                      onChange={e =>
                        setFormData(prevState => ({
                          ...prevState,
                          nome: e.target.value,
                        }))
                      }
                      name={t.formInputTexts.name}
                      placeholder={t.formInputTexts.name}
                      variant="inputs.primary"
                      disabled={loading}
                      required
                    />
                  </Box>
                  <Box sx={{ pb: [4, 4, 4, 4] }}>
                    <Input
                      onChange={e =>
                        setFormData(prevState => ({
                          ...prevState,
                          cognome: e.target.value,
                        }))
                      }
                      name={t.formInputTexts.surname}
                      placeholder={t.formInputTexts.surname}
                      variant="inputs.primary"
                      disabled={loading}
                      required
                    />
                  </Box>
                </Grid>
                <Grid columns={[1, 1, 2]} gap={[0, 0, 3]}>
                  <Box sx={{ pb: [4, 4, 4, 4] }}>
                    <Input
                      onChange={e =>
                        setFormData(prevState => ({
                          ...prevState,
                          email: e.target.value,
                        }))
                      }
                      name="email"
                      type="email"
                      placeholder="Email*"
                      variant="inputs.primary"
                      disabled={loading}
                      required
                    />
                  </Box>
                  <Box sx={{ pb: [4, 4, 4, 4] }}>
                    <Input
                      onChange={e =>
                        setFormData(prevState => ({
                          ...prevState,
                          azienda: e.target.value,
                        }))
                      }
                      name={t.formInputTexts.company}
                      placeholder={t.formInputTexts.company}
                      variant="inputs.primary"
                      disabled={loading}
                      required
                    />
                  </Box>
                </Grid>

                <Box sx={{}}>
                  <LabeledCheckbox
                    checked={privacyAccepted}
                    onChange={e => setPrivacyAccepted(e.target.checked)}
                    required={true}
                  >
                    <Text
                      sx={{
                        mb: 0,
                        fontSize: [0],
                        color: "dark",
                      }}
                    >
                      {t.formInputTexts.privacyPolicy}
                    </Text>
                  </LabeledCheckbox>
                </Box>

                {showValidationError && (
                  <Text
                    sx={{
                      color: "red",
                      fontSize: "14px",
                      mt: 2,
                      mb: 2,
                    }}
                  >
                    {t.validationError}
                  </Text>
                )}

                {!success ? (
                  <Flex sx={{ justifyContent: "flex-end" }}>
                    <Button
                      variant="kicker"
                      type="submit"
                      disabled={loading}
                      sx={{
                        display: "flex",
                        cursor: loading ? "not-allowed" : "pointer",
                        ":hover": {
                          backgroundColor: loading
                            ? "primary"
                            : "secondary",
                        },
                        alignContent: "center",
                        alignItems: "center",
                        gap: 2,
                        mt: [3, 3, 3, 3],
                        borderRadius: "80px",
                        textTransform: "uppercase",
                        opacity: loading ? 0.7 : 1,
                      }}
                    >
                      {loading ? t.loading : t.sendRequestForm}
                      {!loading && (
                        <Box>
                          <Image src={ArrowRight} />
                        </Box>
                      )}
                    </Button>
                  </Flex>
                ) : (
                  <Button
                    type="submit"
                    sx={{
                      mt: [3, 3, 3, 3],
                      color: "primary",
                      backgroundColor: "lightgray",
                    }}
                    variant="kicker"
                    disabled
                  >
                    {t.requestSent}
                  </Button>
                )}
              </Grid>
            </Box>
          </form>
        </Box>
      )}
    </i18nContext.Consumer>
  )
}

export default Survey